import React, { useEffect, useRef, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { ButtonStandard, Chip, RadioButtons, SingleSelectInput, Text } from "@performyard/py-components";
import type { Option } from "@performyard/py-components/src/types/Option";
import { DEFAULT_TIMEZONE } from "@performyard/utils-isomorphic";

import { ValueInput } from "./ValueInput";

import type { DateSubType, IEmployeeField, QueryCondition } from "./types";
import { ALLOWED_FIELD_NAMES, ALLOWED_FIELD_TYPES, DATE_TYPES } from "./types";

import { useCompanySettings } from "../../api/hooks/defs/company";

import {
	adjustDateToDayBoundary,
	detectDateSubType,
	getComparatorsForFieldType,
	getFieldId,
	getFieldType,
	toAPIComparator,
	toUIComparator,
	unwrapRegexValue,
	wrapRegexValue,
} from "./utils";

import { IS_MANAGER_FIELD_ID, IS_MANAGER_FIELD_NAME } from "./constants";

type DraftCondition = {
	fieldId: string | null;
	comparator: string | null;
	value: QueryCondition["value"];
	dateSubType: DateSubType;
};

const createEmptyDraft = (): DraftCondition => ({
	fieldId: null,
	comparator: null,
	value: null,
	dateSubType: "static",
});

const draftFromCondition = (condition: QueryCondition, employeeFields: IEmployeeField[]): DraftCondition => {
	const fieldId = getFieldId(condition.field);
	const fieldType = getFieldType(condition.field, employeeFields);
	const dateSubType = DATE_TYPES.includes(fieldType) ? detectDateSubType(condition.value) : "static";
	const uiComparator = toUIComparator(condition.comparator, condition.value);

	let value = condition.value;
	if ((condition.comparator === "regex" || condition.comparator === "not_regex") && typeof value === "string") {
		value = unwrapRegexValue(value);
	}
	if (typeof value === "object" && value !== null) {
		if (dateSubType === "relative" && "relative_date" in value) {
			value = { ...value, relative_date: Math.abs(value.relative_date) };
		} else if (dateSubType === "anniversary" && "anniversary_date" in value) {
			value = { ...value, anniversary_date: Math.abs(value.anniversary_date) };
		}
	}

	return { fieldId, comparator: uiComparator, value, dateSubType };
};

const draftToCondition = (draft: DraftCondition, timezone: string): QueryCondition | null => {
	if (!draft.fieldId || !draft.comparator) return null;

	const field: QueryCondition["field"] =
		draft.fieldId === IS_MANAGER_FIELD_ID ? IS_MANAGER_FIELD_ID : { oid: draft.fieldId };

	const apiComparator = toAPIComparator(draft.comparator);

	let apiValue = draft.value;
	if (
		(draft.comparator.startsWith("regex_") || draft.comparator.startsWith("not_regex_")) &&
		typeof apiValue === "string"
	) {
		apiValue = wrapRegexValue(draft.comparator, apiValue);
	}
	if (typeof apiValue === "object" && apiValue !== null) {
		if ("relative_date" in apiValue) {
			apiValue = {
				relative_date: Math.abs(apiValue.relative_date) * -1,
				relative_date_unit: apiValue.relative_date_unit,
				timezone,
			};
		} else if ("anniversary_date" in apiValue) {
			apiValue = {
				anniversary_date: Math.abs(apiValue.anniversary_date) * -1,
				anniversary_date_unit: apiValue.anniversary_date_unit,
				timezone,
			};
		} else if ("date" in apiValue) {
			const date = new Date(apiValue.date);
			apiValue = { date: adjustDateToDayBoundary(date, apiComparator, timezone) };
		}
	} else if (apiComparator === "eq_anniversary") {
		apiValue = {
			anniversary_date: 0,
			anniversary_date_unit: "days",
			timezone,
		};
	}

	return {
		field,
		comparator: apiComparator as QueryCondition["comparator"],
		value: apiValue as QueryCondition["value"],
	};
};

const isDraftComplete = (draft: DraftCondition, fieldType: string, comparator: string | null): boolean => {
	if (!draft.fieldId || !draft.comparator) return false;
	if (fieldType === "boolean" && (draft.value === true || draft.value === false)) return true;
	if (comparator === "eq_anniversary") return true;
	if (draft.value === null || draft.value === undefined) return false;
	if (typeof draft.value === "string" && draft.value === "") return false;
	return true;
};

type ConditionEditorProps = {
	employeeFields: IEmployeeField[];
	onSave: (conditions: QueryCondition[]) => void;
	editingConditions?: QueryCondition[];
	enableAnniversary?: boolean;
	fieldSelectPlaceholder?: string;
};

const DATE_SUB_TYPE_OPTIONS: Option<DateSubType>[] = [
	{ value: "static", label: "Static" },
	{ value: "relative", label: "Relative" },
];

const DATE_SUB_TYPE_OPTIONS_WITH_ANNIVERSARY: Option<DateSubType>[] = [
	...DATE_SUB_TYPE_OPTIONS,
	{ value: "anniversary", label: "Anniversary" },
];

export const ConditionEditor = ({
	employeeFields,
	onSave,
	editingConditions,
	enableAnniversary = false,
	fieldSelectPlaceholder = "Select a field",
}: ConditionEditorProps) => {
	const [drafts, setDrafts] = useState<DraftCondition[]>([createEmptyDraft()]);
	const isEditing = !!editingConditions;

	const { data: companySettings } = useCompanySettings();
	const companyTimezone = companySettings?.default_timezone ?? DEFAULT_TIMEZONE;

	const employeeFieldsRef = useRef(employeeFields);
	employeeFieldsRef.current = employeeFields;

	useEffect(() => {
		if (editingConditions && editingConditions.length > 0) {
			setDrafts(editingConditions.map((c) => draftFromCondition(c, employeeFieldsRef.current)));
		} else {
			setDrafts([createEmptyDraft()]);
		}
	}, [editingConditions]);

	const fieldOptions: Option<string>[] = [
		{ value: IS_MANAGER_FIELD_ID, label: IS_MANAGER_FIELD_NAME },
		...employeeFields
			.filter((f) => ALLOWED_FIELD_TYPES.includes(f.type) || ALLOWED_FIELD_NAMES.includes(f.name))
			.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
			.map((f) => ({ value: f._id, label: f.name })),
	];

	const getFieldTypeForDraft = (draft: DraftCondition): string => {
		if (!draft.fieldId) return "";
		if (draft.fieldId === IS_MANAGER_FIELD_ID) return "boolean";
		const found = employeeFields.find((f) => f._id === draft.fieldId);
		return found?.type ?? "";
	};

	const getFieldOptionsForDraft = (draft: DraftCondition): string[] => {
		if (!draft.fieldId || draft.fieldId === IS_MANAGER_FIELD_ID) return [];
		const found = employeeFields.find((f) => f._id === draft.fieldId);
		return found?.options ?? [];
	};

	const updateDraft = (index: number, partial: Partial<DraftCondition>) => {
		setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...partial } : d)));
	};

	const handleFieldChange = (index: number, fieldId: string | null) => {
		if (!fieldId) return;

		const fieldType =
			fieldId === IS_MANAGER_FIELD_ID ? "boolean" : (employeeFields.find((f) => f._id === fieldId)?.type ?? "");

		const dateSubType: DateSubType = "static";
		const comparators = getComparatorsForFieldType(fieldType, dateSubType);
		const defaultComparator = comparators[0]?.value ?? null;

		if (index === 0 && drafts.length > 1) {
			setDrafts([{ fieldId, comparator: defaultComparator, value: null, dateSubType }]);
		} else {
			updateDraft(index, { fieldId, comparator: defaultComparator, value: null, dateSubType });
		}
	};

	const handleDateSubTypeChange = (index: number, newSubType: DateSubType) => {
		const draft = drafts[index];
		const fieldType = getFieldTypeForDraft(draft);
		const comparators = getComparatorsForFieldType(fieldType, newSubType);
		updateDraft(index, {
			dateSubType: newSubType,
			comparator: comparators[0]?.value ?? null,
			value: null,
		});
	};

	const handleAddOr = () => {
		const firstDraft = drafts[0];
		if (!firstDraft?.fieldId) return;
		const fieldType = getFieldTypeForDraft(firstDraft);
		const comparators = getComparatorsForFieldType(fieldType, firstDraft.dateSubType);
		setDrafts((prev) => [
			...prev,
			{
				fieldId: firstDraft.fieldId,
				comparator: comparators[0]?.value ?? null,
				value: null,
				dateSubType: firstDraft.dateSubType,
			},
		]);
	};

	const handleRemove = (index: number) => {
		if (drafts.length > 1) {
			setDrafts((prev) => prev.filter((_, i) => i !== index));
		} else {
			setDrafts([createEmptyDraft()]);
		}
	};

	const handleSave = () => {
		const conditions = drafts
			.map((d) => draftToCondition(d, companyTimezone))
			.filter((c): c is QueryCondition => c !== null);
		if (conditions.length > 0) {
			onSave(conditions);
			setDrafts([createEmptyDraft()]);
		}
	};

	const allComplete = drafts.every((draft) => {
		const fieldType = getFieldTypeForDraft(draft);
		return isDraftComplete(draft, fieldType, draft.comparator);
	});

	return (
		<div className="flex h-fit w-full flex-col rounded border border-solid border-neutral-3 p-4">
			{drafts.map((draft, index) => {
				const fieldType = getFieldTypeForDraft(draft);
				const isDateType = DATE_TYPES.includes(fieldType);
				const comparatorOptions = getComparatorsForFieldType(fieldType, draft.dateSubType);
				const isLast = index === drafts.length - 1;
				const dateSubTypeOptions = enableAnniversary
					? DATE_SUB_TYPE_OPTIONS_WITH_ANNIVERSARY
					: DATE_SUB_TYPE_OPTIONS;

				return (
					<div key={index}>
						<div className="mb-4 flex items-center justify-between">
							<Text
								size="paragraph-2"
								weight="semi-bold"
								color="neutral-9"
								dangerouslySetClassName="whitespace-nowrap"
							>
								{isEditing ? "Edit Condition" : "Add Condition"}
							</Text>
							<div>
								<ButtonStandard
									size="small"
									variant="micro"
									color="red"
									onClick={() => handleRemove(index)}
								>
									<Text size="caption" weight="semi-bold">
										Clear
									</Text>
								</ButtonStandard>
							</div>
						</div>

						<div className="mt-2">
							<SingleSelectInput
								options={fieldOptions}
								value={draft.fieldId ?? undefined}
								onChange={(val) => handleFieldChange(index, val)}
								placeholder={fieldSelectPlaceholder}
								disabled={index > 0}
								id={`field-select-${index}`}
							/>
						</div>

						{draft.fieldId && (
							<div className="mt-2">
								{isDateType && (
									<div className="mb-2 py-2">
										<Text size="paragraph-3" weight="semi-bold">
											Date Type
										</Text>
										<div className="mt-1">
											<RadioButtons
												options={dateSubTypeOptions}
												value={draft.dateSubType}
												onChange={(val) => handleDateSubTypeChange(index, val)}
												orientation="horizontal"
												id={`date-sub-type-${index}`}
											/>
										</div>
									</div>
								)}

								<SingleSelectInput
									options={comparatorOptions}
									value={draft.comparator ?? undefined}
									onChange={(val) => {
										if (val) {
											updateDraft(index, { comparator: val });
										}
									}}
									placeholder="Select a comparator"
									id={`comparator-select-${index}`}
								/>

								<div className="mt-2">
									<ValueInput
										fieldType={fieldType}
										dateSubType={isDateType ? draft.dateSubType : undefined}
										comparator={draft.comparator ?? ""}
										value={draft.value}
										onChange={(val) => updateDraft(index, { value: val })}
										fieldOptions={getFieldOptionsForDraft(draft)}
										companyTimezone={companyTimezone}
										index={index}
									/>
								</div>
							</div>
						)}

						{draft.fieldId && (
							<div className={isLast ? "mt-4" : "my-4"}>
								{isLast ? (
									<div className="w-fit">
										<ButtonStandard
											color="green"
											size="small"
											onClick={handleAddOr}
											dangerouslySetClassName="rounded-[16px]"
											iconLeft={faPlus}
											disabled={!isDraftComplete(draft, fieldType, draft.comparator)}
										>
											<div className="mr-1">
												<Text size="caption" weight="semi-bold">
													OR
												</Text>
											</div>
										</ButtonStandard>
									</div>
								) : (
									<Chip label="OR" color="neutral" size={24} />
								)}
							</div>
						)}
					</div>
				);
			})}

			<div className="mt-4">
				{/* Block save until company settings resolve so date conditions persist the real
				    company timezone, not the DEFAULT_TIMEZONE fallback (PY-13538). */}
				<ButtonStandard onClick={handleSave} disabled={!allComplete || !companySettings}>
					{isEditing ? "Update Condition" : "Add Condition"}
				</ButtonStandard>
			</div>
		</div>
	);
};
